import { createSlice } from "@reduxjs/toolkit";
import {
  TOOL_GUIDE_ROOT,
  BUCKET_KEYS,
  getToolGuideForRole,
  buildStepPath,
  getNextStep,
} from "../../../features/toolGuide/toolGuideConfig";

const initialState = {
  isActive: false,
  isPanelOpen: false,
  /** Auth level used to resolve guide content (e.g. L3) */
  roleKey: "",
  /** Current breadcrumb segments */
  path: [],
  /** Active bucket display name */
  bucketName: "",
  /** Active action step id from config (optional) */
  activeStepId: "",
};

const toolGuideSlice = createSlice({
  name: "toolGuide",
  initialState,
  reducers: {
    setToolGuideActive(state, action) {
      state.isActive = Boolean(action.payload);
    },
    setToolGuidePanelOpen(state, action) {
      state.isPanelOpen = Boolean(action.payload);
    },
    setToolGuideRole(state, action) {
      state.roleKey = action.payload || "";
    },
    /**
     * Set bucket context for Tool Guide.
     * payload: { bucketName, roleKey?, openPanel? }
     * openPanel: true when a bucket is opened so the Flow card shows in the sidebar.
     */
    setToolGuideBucket(state, action) {
      const {
        bucketName = "",
        roleKey,
        openPanel = false,
      } = action.payload || {};

      if (roleKey) state.roleKey = roleKey;

      const resolvedRole = state.roleKey;
      const guide = getToolGuideForRole(resolvedRole, bucketName);
      const bucketChanged = state.bucketName !== bucketName;

      state.bucketName = bucketName;

      if (bucketChanged) {
        state.activeStepId = "";
        if (guide?.pathBase?.length) {
          state.path = [...guide.pathBase];
        } else if (bucketName) {
          state.path = [TOOL_GUIDE_ROOT, bucketName];
        } else {
          state.path = [];
        }
      }

      if (openPanel && bucketName) {
        state.isPanelOpen = true;
        state.isActive = true;
      } else if (!bucketName) {
        state.isPanelOpen = false;
      }
    },
    /**
     * Set action step — builds Explorer-style path trail.
     * e.g. Forward → My Requests / Outstanding / Assign Task to Managers / Forward
     * payload: { stepId?, pathLabel?, openPanel? }
     */
    setToolGuideStep(state, action) {
      const { stepId = "", pathLabel = "", openPanel = false } = action.payload || {};
      const guide = getToolGuideForRole(state.roleKey, state.bucketName);

      state.activeStepId = stepId || "";
      state.path = buildStepPath(guide, stepId, pathLabel);
      if (openPanel) state.isPanelOpen = true;
    },
    /**
     * Advance the sidebar Flow card to the next instructional step.
     * After the last step, returns to the bucket overview.
     */
    advanceToolGuideStep(state) {
      const guide = getToolGuideForRole(state.roleKey, state.bucketName);
      const next = getNextStep(guide, state.activeStepId);

      if (!next) {
        state.activeStepId = "";
        state.path = guide?.pathBase?.length
          ? [...guide.pathBase]
          : state.bucketName
            ? [TOOL_GUIDE_ROOT, state.bucketName]
            : [];
        return;
      }

      state.activeStepId = next.id;
      state.path = buildStepPath(guide, next.id, next.pathLabel);
      state.isPanelOpen = true;
    },
    clearToolGuide(state) {
      state.isActive = false;
      state.isPanelOpen = false;
      state.path = [];
      state.bucketName = "";
      state.activeStepId = "";
    },
    clearToolGuidePath(state) {
      state.path = [];
      state.bucketName = "";
      state.activeStepId = "";
      state.isPanelOpen = false;
    },
  },
});

export const {
  setToolGuideActive,
  setToolGuidePanelOpen,
  setToolGuideRole,
  setToolGuideBucket,
  setToolGuideStep,
  advanceToolGuideStep,
  clearToolGuide,
  clearToolGuidePath,
} = toolGuideSlice.actions;

export { BUCKET_KEYS };

export default toolGuideSlice.reducer;
